import React, { useState, useRef, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Components
import ModalContainer from '../../containers/ModalContainer';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';

// Styled Components
import {
  ModalContent,
  ScrollableContent,
  FormContainer,
  InputContainer,
  StyledInputText,
  SectionTitle,
  SelectionList,
  SelectionItem,
  SelectionCheckbox,
  SelectionLabel,
  EmptyMessage,
  StickyFooter,
  CancelButton,
  SubmitButton,
  DeleteButton,
} from './styles';

// Types
import { ModalProps, EditTeamFormType } from './types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';
import { Options } from '../../../inputs/DropDown/DropDownList/types';

// Schema
import { EditTeamFormSchema } from './schema';

// GraphQL
import { client } from '../../../../gql/client';
import { UPDATE_TEAM } from '../../../../gql/mutations/teams';

// Sentry
import * as Sentry from '@sentry/react';

// Media
import { useIsLandscapeTablet } from '../../../../hooks/useMediaBreakPoints';

// Utils
import { showSuccessToast, showErrorToast } from '../../../../utils/toasts';

export const EditTeamModal = ({
  onClickClose,
  data,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['forms', 'common', 'management']);
  const isLandscapeTablet = useIsLandscapeTablet();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();

  // Refs
  const concessionRef = useRef<ImperativeRefInput>(null);

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
    watch,
    setValue,
  } = useForm<EditTeamFormType>({
    mode: 'onTouched',
    reValidateMode: 'onBlur',
    defaultValues: {
      name: data?.team?.name || '',
      concessionIDs: data?.team?.concessionIDs || [],
      roadIDs: data?.team?.roadIDs || [],
      userIDs: data?.team?.userIDs || [],
      vehicleIDs: data?.team?.vehicleIDs || [],
    },
    resolver: yupResolver(EditTeamFormSchema) as any,
  });

  // Watch selected values for multi-select lists
  const selectedUserIDs = watch('userIDs') || [];
  const selectedVehicleIDs = watch('vehicleIDs') || [];

  // Concession options
  const concessionOptions: Options = useMemo(
    () =>
      data?.concessions?.map((c) => ({
        label: c.name,
        value: c.id,
      })) || [],
    [data?.concessions]
  );

  // Road options
  const roadOptions: Options = useMemo(
    () =>
      data?.roads?.map((r) => ({
        label: r.name,
        value: r.id,
      })) || [],
    [data?.roads]
  );

  const onSubmit = async (formData: EditTeamFormType) => {
    if (!data?.team?.id) return;

    setIsSubmitting(true);

    try {
      await client.mutate({
        mutation: UPDATE_TEAM,
        variables: {
          input: {
            id: data.team.id,
            name: formData.name,
            concessionIDs: formData.concessionIDs,
            roadIDs: formData.roadIDs,
            userIDs: formData.userIDs,
            vehicleIDs: formData.vehicleIDs,
          },
        },
      });

      showSuccessToast(t('management:teams.updateSuccess', 'Equipo actualizado correctamente'));

      // Call success callback if provided
      if (data?.onSuccess) {
        data.onSuccess();
      }

      onClickClose();
    } catch (err) {
      Sentry.captureException(err);
      console.log('Error updating team:', err);

      const errorMessage = (err as Error)?.message;

      if (errorMessage?.includes('Team not found')) {
        showErrorToast(t('management:teams.notFound', 'Equipo no encontrado'));
      } else if (errorMessage?.includes('already exists')) {
        showErrorToast(t('management:teams.nameExists', 'Ya existe un equipo con este nombre'));
      } else {
        showErrorToast(t('management:teams.updateError', 'Error al actualizar el equipo'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle user checkbox toggle
  const handleUserToggle = (userId: string) => {
    const current = selectedUserIDs || [];
    if (current.includes(userId)) {
      setValue(
        'userIDs',
        current.filter((id) => id !== userId)
      );
    } else {
      setValue('userIDs', [...current, userId]);
    }
  };

  // Handle vehicle checkbox toggle
  const handleVehicleToggle = (vehicleId: string) => {
    const current = selectedVehicleIDs || [];
    if (current.includes(vehicleId)) {
      setValue(
        'vehicleIDs',
        current.filter((id) => id !== vehicleId)
      );
    } else {
      setValue('vehicleIDs', [...current, vehicleId]);
    }
  };

  return (
    <ModalContainer
      width={60}
      onClickClose={onClickClose}
      fill={theme.colors.black}
      title={t('management:teams.editTitle', 'Editar Equipo')}
    >
      <ModalContent>
        <ScrollableContent>
          <FormContainer
            id="edit-team-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <InputContainer>
            <StyledInputText
              {...register('name')}
              label={t('common:labels.name', 'Nombre')}
              labelSize={isLandscapeTablet ? 'XS' : 'S'}
              typeInput="normal"
              placeholder={t('management:teams.namePlaceholder', 'Nombre del equipo')}
              error={errors.name?.message}
            />

            <Controller
              control={control}
              name="concessionIDs"
              render={({ field: { onChange, value } }) => {
                // Si solo hay una concesión, auto-seleccionar y no mostrar el selector
                if (concessionOptions.length === 1) {
                  const concessionId = concessionOptions[0].value;
                  if (!value || value.length === 0 || value[0] !== concessionId) {
                    onChange([concessionId]);
                  }
                  return <></>;
                }
                return (
                  <DropDownInput
                    placeholder={t('common:labels.select', 'Seleccionar')}
                    labelText={t('user.concession', 'Concesión')}
                    defaultValue={value?.[0] || ''}
                    inputSize="M"
                    typeDropDown="Default"
                    options={concessionOptions}
                    onChangeSelected={(option) =>
                      onChange(option?.value ? [option.value] : [])
                    }
                    borderError={!!errors.concessionIDs?.message}
                    ref={concessionRef}
                  />
                );
              }}
            />

            <Controller
              control={control}
              name="roadIDs"
              render={({ field: { onChange, value } }) => (
                <DropDownInput
                  placeholder={t('common:labels.select', 'Seleccionar')}
                  labelText={t('management:teams.road', 'Carretera')}
                  defaultValue={value?.[0] || ''}
                  inputSize="M"
                  typeDropDown="Default"
                  options={roadOptions}
                  onChangeSelected={(option) =>
                    onChange(option?.value ? [option.value] : [])
                  }
                  borderError={!!errors.roadIDs?.message}
                />
              )}
            />
          </InputContainer>

          {/* Users Selection */}
          <SectionTitle variant="semiBold">
            {t('management:teams.assignedUsers', 'Usuarios asignados')}
          </SectionTitle>
          <SelectionList>
            {data?.users && data.users.length > 0 ? (
              data.users.map((user) => (
                <SelectionItem key={user.id}>
                  <SelectionCheckbox
                    checked={selectedUserIDs.includes(user.id)}
                    onChange={() => handleUserToggle(user.id)}
                  />
                  <SelectionLabel>
                    {user.name} {user.surName}
                  </SelectionLabel>
                </SelectionItem>
              ))
            ) : (
              <EmptyMessage>
                {t('management:teams.noUsersAvailable', 'No hay usuarios disponibles')}
              </EmptyMessage>
            )}
          </SelectionList>

          {/* Vehicles Selection */}
          <SectionTitle variant="semiBold">
            {t('management:teams.assignedVehicles', 'Vehículos asignados')}
          </SectionTitle>
          <SelectionList>
            {data?.vehicles && data.vehicles.length > 0 ? (
              data.vehicles.map((vehicle) => (
                <SelectionItem key={vehicle.id}>
                  <SelectionCheckbox
                    checked={selectedVehicleIDs.includes(vehicle.id)}
                    onChange={() => handleVehicleToggle(vehicle.id)}
                  />
                  <SelectionLabel>{vehicle.plate}</SelectionLabel>
                </SelectionItem>
              ))
            ) : (
              <EmptyMessage>
                {t('management:teams.noVehiclesAvailable', 'No hay vehículos disponibles')}
              </EmptyMessage>
            )}
          </SelectionList>
          </FormContainer>
        </ScrollableContent>

        <StickyFooter>
          {data?.onDelete && (
            <DeleteButton
              type="button"
              text={t('common:buttons.delete', 'Eliminar')}
              onClick={data.onDelete}
              disabled={isSubmitting}
              variant="secondary"
            />
          )}
          <CancelButton
            type="button"
            text={t('common:buttons.cancel', 'Cancelar')}
            variant="secondary"
            onClick={onClickClose}
            disabled={isSubmitting}
          />
          <SubmitButton
            type="submit"
            form="edit-team-form"
            text={
              isSubmitting
                ? t('toasts:info.saving')
                : t('common:buttons.save', 'Guardar')
            }
            disabled={isSubmitting}
          />
        </StickyFooter>
      </ModalContent>
    </ModalContainer>
  );
};
